import {
  AccountsIcon,
  BlobLibraryIcon,
  CommentIcon,
  CourseIcon,
  CreateIcon,
  FeedIcon,
  LibraryIcon,
  LogoIcon,
  MymeLocationBasedIcon,
  MymeTimeBasedIcon,
  ReportIcon
} from 'src/icons';
import { UserRole } from 'src/contexts/AuthContext/types';
import { useAuth } from 'src/hooks';
import {
  blobsLibraryPageRoute,
  feedPageRoute,
  libraryPageRoute,
  myCoursesPageRoute,
  myLocationBasedMymesPageRoute,
  mymeEditorCreatorStepPageRoute,
  myTimeBasedMymesPageRoute,
  superAdminAccountsPageRoute,
  superAdminMymesPageRoute,
  superAdminReportedMymesPageRoute,
  superAdminReportedReviewsPageRoute,
  superAdminReviewsPageRoute
} from 'src/routes/route-list';

type NavSectionText =
  | 'feed'
  | 'library'
  | 'myme_time_based'
  | 'myme_location_based'
  | 'myme_courses'
  | 'myme_create'
  | 'blob_library'
  | 'settings'
  | 'sales'
  | 'all_comments'
  | 'reported_comments'
  | 'all_mymes'
  | 'reported_mymes'
  | 'accounts';

interface NavSectionItem {
  to: string;
  icon: JSX.Element;
  text: NavSectionText;
  disabled?: boolean;
}

interface NavSection {
  name: 'header_menu' | 'header_mymes' | 'header_payments' | 'header_superadmin';
  items: NavSectionItem[];
}

const mainSections: NavSection = {
  name: 'header_menu',
  items: [
    {
      to: feedPageRoute,
      icon: <FeedIcon />,
      text: 'feed'
    },
    {
      to: libraryPageRoute,
      icon: <LibraryIcon />,
      text: 'library'
    }
  ]
};

const mymeSections: NavSection = {
  name: 'header_mymes',
  items: [
    {
      to: myTimeBasedMymesPageRoute,
      icon: <MymeTimeBasedIcon fill='#fff' />,
      text: 'myme_time_based'
    },
    {
      to: myLocationBasedMymesPageRoute,
      icon: <MymeLocationBasedIcon fill='#fff' />,
      text: 'myme_location_based'
    },
    {
      to: myCoursesPageRoute,
      icon: <CourseIcon />,
      text: 'myme_courses'
    },
    {
      to: mymeEditorCreatorStepPageRoute,
      icon: <CreateIcon />,
      text: 'myme_create'
    },
    {
      to: blobsLibraryPageRoute,
      icon: <BlobLibraryIcon />,
      text: 'blob_library'
    }
  ]
};

const superAdminSections: NavSection = {
  name: 'header_superadmin',
  items: [
    {
      to: superAdminReviewsPageRoute,
      icon: <CommentIcon />,
      text: 'all_comments'
    },
    {
      to: superAdminReportedReviewsPageRoute,
      icon: <ReportIcon />,
      text: 'reported_comments'
    },
    {
      to: superAdminMymesPageRoute,
      icon: <LogoIcon />,
      text: 'all_mymes'
    },
    {
      to: superAdminReportedMymesPageRoute,
      icon: <ReportIcon />,
      text: 'reported_mymes'
    },
    {
      to: superAdminAccountsPageRoute,
      icon: <AccountsIcon />,
      text: 'accounts'
    }
  ]
};

const getNavSections = (role: UserRole) => {
  switch (role) {
    case 'ADMIN': {
      return [mainSections, mymeSections, superAdminSections];
    }
    case 'USER-PREMIUM': {
      return [mainSections, mymeSections];
    }
    case 'USER':
    default: {
      return [mainSections];
    }
  }
};

export const useGetNavbarSections = () => {
  const { user } = useAuth();

  if (!user) {
    return [];
  }

  return getNavSections(user.role);
};
